import React, { forwardRef, Fragment, useState, useEffect, useRef, useImperativeHandle } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { 
  getBasicInfo,
  setBasicInfo,
  setSessionStorageItem,
  getSessionStorageItem,
  SESSION_STORAGE_ACCOUNT_KEY,
  SESSION_STORAGE_EMAIL_CAPTURE,
  SESSION_STORAGE_SAVED_QUOTE_DATA,
} from '../../../sessionStorage';
import { FormProvider, useForm } from 'react-hook-form';
import { createTheme, ThemeProvider } from '@mui/material';
import { sub } from 'date-fns';
import { yupResolver } from "@hookform/resolvers/yup";
import { customValidators, object, string, date } from '../../Helpers/Validators/customValidators';
import States from '../../Helpers/Lists/States';
import StatesWithDisabled from '../../Helpers/Lists/StatesWithDisabled';
import GetHealthClassFromBmi from '../Digital/Helpers/GetHealthClassFromBmi';
import { InputMaskHeight, InputMaskWeight, InputMaskDate } from '../../Helpers/Formatters';
import { CheckboxInput, TextInput, SelectInput, StyledRadioInput } from '../../Helpers/Inputs';
import TobaccoUse from '../../Helpers/Lists/TobaccoUse';
import BackNext from '../Common/SubComponents/BackNext';
import { emptyStringToUndefined } from '../../Helpers/Formatters/EmptyStringToUndefined';
import { calculateBMI } from '../../Helpers/Conversion';
import { calculateExactAge, calculateNearestAge } from '../Common/Helpers/AgeCalculation';
import ModalWarning from '../../Helpers/Widgets/ModalWarning';
import './BasicInfo.scss';

const BasicInfo = forwardRef((props, ref) => {
  const innerRef = useRef(null);
  const emailCapture = getSessionStorageItem(SESSION_STORAGE_EMAIL_CAPTURE);
  const { isPartnerReferralEmailCaptureActive, enablePartnerReferralEmailCapture } = emailCapture;

  const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const savedQuote = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);
  const basicInfo = getBasicInfo();

  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [saveQuoteData, setSaveQuoteData] = useState(null);
  const [saveQuoteContact, setSaveQuoteContact] = useState(savedQuote);
  const [isQuoteSaved, setQuoteSaved] = useState(savedQuote.quoteSaved);

  const theme = createTheme({
    palette: {
      primary: {
        main: `${props.brandTheme ? props.brandTheme : "#3f51b5"}`,
      },
    },
    typography: {
      fontFamily: "museo-sans",
      body1: {
        fontSize: 14,
        fontWeight: 500,
      },
    },
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontSize: 16,
          },
          root: {
            height: 56,
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
          },
          underline: {
            "&::before": {
              borderBottom: "solid 2px #b2b2b2",
            },
            "&:hover::before": {
              borderBottomWidth: "2px",
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: '16px !important',
            fontWeight: '500 !important',
            color: "rgba(0,0,0,0.6)",
            '&.Mui-error': {
              color: '#f44336 !important',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginTop: 0,
            paddingTop: 0,
            fontSize: '0.75rem !important',
            '&.Mui-error': {
              color: '#f44336 !important',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          shrink: {
            color: "rgba(0,0,0,0.3) !important",
          },
        },
      },
      MuiNativeSelect: {
        styleOverrides: {
          fontSize: 16,
          icon: {
            color: "rgba(0,0,0,1)",
          },
          iconFilled: {
            right: 16,
          },
        },
      },
    },
  });
  const formMethods = useForm({
    resolver: yupResolver(BasicInfo.ValidationSchema(props.ageSpecs)),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState,
  });
  const { handleSubmit, trigger, register, setValue, formState: { isSubmitted, touchedFields } } = formMethods;

  useEffect(() => {
    register('isJuvenile');
    setValue('isJuvenile', props.juviMode);
  }, [register]);

  const [purlMismatch, setPurlMismatch] = useState(false);
  const [initialBasicInfo] = useState(props.formState);
  const birthdateInteracted = useRef(false);

  useEffect(() => {
    (async () => {
      if (props.isPurlVerification && props.selectedQuote && !initialBasicInfo.birthdate) {
        let mismatch = false;
        if (props.formState.birthdate !== '' && (await trigger('birthdate'))) {
          let initialAge, newAge;
          if (props.selectedQuote.ageDetermination && props.selectedQuote.ageDetermination.toLowerCase() === 'nearest') {
            initialAge = calculateNearestAge(new Date(initialBasicInfo.fakeBirthdate));
            newAge = calculateNearestAge(new Date(props.formState.birthdate));
          } else {
            initialAge = calculateExactAge(new Date(initialBasicInfo.fakeBirthdate));
            newAge = calculateExactAge(new Date(props.formState.birthdate));
          }
          mismatch = mismatch || initialAge !== newAge;
        }
        mismatch = mismatch || initialBasicInfo.gender !== props.formState.gender;
        mismatch = mismatch || initialBasicInfo.state !== props.formState.state;
        mismatch = mismatch || initialBasicInfo.tobacco !== props.formState.tobacco;
        setPurlMismatch(mismatch);
      }
    })();
  }, [props.formState]);

  const handleValueChange = (field, value) => {
    props.onFieldChange(field, value);
    if (field === 'gender' || field === 'tobacco') {
      trigger(field);
    } else if (field === 'birthdate') {
      if (isSubmitted || touchedFields.birthdate || birthdateInteracted.current) {
        trigger(field);
      }
    }
  };

  useEffect(() => {
    setSubmitDisabled(!!isPartnerReferralEmailCaptureActive && !!enablePartnerReferralEmailCapture);
    if (submitDisabled) {
      props.setEmailCaptureActive(submitDisabled);
    }
  }, [register]);

  function calculateTotalHeight() {
    const radix = 10;
    const heightArray = props.formState.height.split(" ");
    const heightFeet = parseInt(heightArray[0], radix);
    let heightInches;
    for (let i = 1; i < heightArray.length; i++) {
      if (heightArray[i] !== "" && heightArray[i] !== "ft") {
        heightInches = parseInt(heightArray[i], radix);
        break;
      }
    };
    return heightFeet * 12 + heightInches;
  };

  useImperativeHandle(ref, () =>({
    onSubmit,
    dispatchSubmit: () => {
      innerRef.current?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
    get element() {
      return innerRef.current;
    }
  }));

  const setCurrentSavedQuoteData = () => {
    const newData = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);
    setSaveQuoteContact(newData);
  };

  useEffect(() => {
    if (props.formState.height && props.formState.weight) {
      const weight = props.formState.weight.split(" ")[0];
      const totalHeightInches = calculateTotalHeight();
      const calculatedBMI = calculateBMI(totalHeightInches, weight);
      const healthClassFromBmi = GetHealthClassFromBmi(calculatedBMI);
      handleValueChange("bmi", calculatedBMI);
      handleValueChange("healthClass", healthClassFromBmi);

    }
  }, [props.formState.height, props.formState.weight]);

  useEffect(() => {
    if (saveQuoteContact?.emailAddress !== "") {
      setSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA, {
        "quoteSaved": isQuoteSaved,
        "firstName": saveQuoteContact?.firstName,
        "lastName": saveQuoteContact?.lastName,
        "emailAddress": saveQuoteContact?.emailAddress,
      });
    }
    setCurrentSavedQuoteData();
  }, [isQuoteSaved]);

  const trimString = (str) => {
    if (str !== null) {
      return str?.replace(/\s+/g, " ");
    }
    return "";
  };

  useEffect(() => {
    setSaveQuoteContact(props.saveQuoteContact);
  }, [props.saveQuoteContact]);

  useEffect(() => {
    if (isPartnerReferralEmailCaptureActive) {
      props.setEmailCaptureActive?.(submitDisabled);
    }
  }, [submitDisabled]);

  const covrDisclaimer = 'Insurance products are sold through Covr Financial Technologies Inc ("Covr"). Covr sells insurance as an agent or broker by contractual agreements with its insurance carrier providers. Covr is licensed in the following states: Delaware (3000067942), Idaho (607086), California (0L77229).';

  useEffect(() => {
    const quoteData = {
      accountId: accountObj.account.accountId,
      accountName: accountObj.account.accountName,
      oneClickAccountId: accountObj.account.oneClickAccountId,
      accountProductTypeSettingId: props.accountProductTypeSettings?.accountProductTypeSettingId,
      productType: props.juviMode ? "securiancwl" : "securianwl",
      sessionId: accountObj.sessionId,
      accountColorLogoUrl: accountObj.account.logoUrl,
      firstName: saveQuoteContact?.firstName,
      lastName: saveQuoteContact?.lastName,
      emailAddress: saveQuoteContact?.emailAddress,
      gender: props.formState.gender,
      dateOfBirth: props.formState.birthdate,
      state: props.formState.state,
      height: trimString(props.formState.height),
      weight: trimString(props.formState.weight),
      tobaccoStatus: false,
      coverageAmount: 0,
      termLength: 0,
      healthClass: "PP",
      quoteLowestPrice: null,
      quoteFastestProcess: null,
      quoteMostPopular: null,
      quotedLowestPrice: null,
      signatureEmail: "insuranceapplication@covrtech.com",
      signaturePhone: accountObj.account.phone,
      covrDisclaimer: covrDisclaimer,
      disclaimerHtml: accountObj.account.disclaimerHtml ? accountObj.account.disclaimerHtml : null,
    };
    setSaveQuoteData(quoteData);
  }, [saveQuoteContact]);

  useEffect(() => {
      if (submitDisabled) {
        props.setSaveQuoteData?.(saveQuoteData);
      }
  }, [saveQuoteData]);

  const onSubmit = () => {

    const totalHeight = calculateTotalHeight();
    const weight = props.formState.weight.split(" ")[0];

    const basicInfoFromSessionStorage = getBasicInfo();
    basicInfoFromSessionStorage.firstName = saveQuoteContact?.firstName || basicInfoFromSessionStorage.firstName;
    basicInfoFromSessionStorage.lastName = saveQuoteContact?.lastName || basicInfoFromSessionStorage.lastName;
    basicInfoFromSessionStorage.gender = props.formState.gender;
    basicInfoFromSessionStorage.birthdate = props.formState.birthdate;
    basicInfoFromSessionStorage.state = props.formState.state;
    basicInfoFromSessionStorage.height = props.formState.height;
    basicInfoFromSessionStorage.weight = props.formState.weight;
    basicInfoFromSessionStorage.healthClass = props.formState.healthClass;
    if (props.tobaccoToTrueFalseString != null) {
      basicInfoFromSessionStorage.tobacco = props.tobaccoToTrueFalseString(props.formState.tobacco);
    } else {
      basicInfoFromSessionStorage.tobacco = props.formState.tobacco;
    }
    basicInfoFromSessionStorage.usResident = props.formState.usResident;
    basicInfoFromSessionStorage.bmi = props.formState.bmi;
    setBasicInfo(basicInfoFromSessionStorage);

    if (props.validateBMI != null && props.validateBMI(totalHeight, props.formState.bmi, weight) && props.onDeclined != null) {
      props.onDeclined();
    } else {
      if (props.openEditPopUp) {
        props.setOpenEditPopUp(false, purlMismatch);
      } else {

        if (submitDisabled && isPartnerReferralEmailCaptureActive) {
          props.setModalSaveQuoteIsOpen?.(submitDisabled);
        }

        props.onNext();
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="basic-info d-flex justify-content-center">
        <FormProvider {...formMethods}>
          <Form onSubmit={handleSubmit(onSubmit)} ref={innerRef}>
            <p className="iam mx-auto">{props.juviMode ? "My Child is..." : "I am..."}</p>

            <StyledRadioInput
              name="gender"
              id="gender"
              value={props.formState.gender}
              options={[{ value: "F", label: "Female", icon: 'female' }, { value: "M", label: "Male", icon: 'male' }]}
              onChange={e => handleValueChange('gender', e)}
            />

            <div className="mb-3">
              <TextInput
                name="birthdate"
                id="birthdate"
                label="Date of Birth"
                value={props.formState.birthdate}
                onChange={(e) => handleValueChange('birthdate', e.target.value)}
                onFocus={() => { birthdateInteracted.current = true; }}
                onClick={() => { birthdateInteracted.current = true; }}
                onKeyDown={() => { birthdateInteracted.current = true; }}
                InputProps={{
                  inputComponent: InputMaskDate,
                }}
                className="basicInfo-date"
              />
            </div>

            <div className="mb-3">
              <SelectInput
                name="state"
                id="state"
                value={props.formState.state}
                onChange={(e) => handleValueChange('state', e.target.value)}
                label="State"
                selectOptions={props.unavailableStates ? StatesWithDisabled(props.unavailableStates) : States()}
                aria-invalid="true"
                aria-describedby="state"
              />
            </div>

            {!props.juviMode &&
              (props.isTobaccoVerbiage ?
                <div className="mb-1">
                  <StyledRadioInput
                    name="tobacco"
                    id="tobacco"
                    label="Have you used any form of tobacco or nicotine in the last 12 months?"
                    value={props.formState.tobacco}
                    options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                    onChange={e => handleValueChange('tobacco', e)}
                    isBlankIcon={true}
                    isBlankCheckMark={false}
                  />
                </div>
                :
                <Fragment>
                  <div className="mb-3">
                    <SelectInput
                      name="tobacco"
                      id="tobacco"
                      value={props.formState.tobacco}
                      onChange={(e) => handleValueChange('tobacco', e.target.value)}
                      label="Tobacco/Nicotine Use"
                      selectOptions={props.tobaccoOptions || TobaccoUse()}
                      aria-invalid="true"
                      aria-describedby="state"
                    />
                  </div>
                </Fragment>)
            }

            <Row>
              <Col className="ppe-10_5">
                <div className="mb-3">
                  <TextInput
                    name="height"
                    id="height"
                    label="Height"
                    value={props.formState.height}
                    onChange={(e) => handleValueChange('height', e.target.value)}
                    InputProps={{
                      inputComponent: InputMaskHeight,
                    }}
                    className="basicInfo-height"
                  />
                </div>
              </Col>

              <Col className="pps-10_5">
                <div className="mb-3">
                  <TextInput
                    name="weight"
                    id="weight"
                    label="Weight"
                    value={props.formState.weight}
                    onChange={(e) => handleValueChange('weight', e.target.value)}
                    InputProps={{
                      inputComponent: InputMaskWeight,
                    }}
                  />
                </div>
              </Col>
            </Row>

            {props.showPolicyReplacementOption && (
              <div className="mb-3 m-0">
                <CheckboxInput
                  name="replacePolicy"
                  id="replacePolicy"
                  checked={props.formState.replacePolicy}
                  onChange={(e) => props.onFieldChange('replacePolicy', e.target.checked)}
                  label="I am replacing a life insurance policy"
                  color="primary"
                />
              </div>
            )}

            {purlMismatch &&
              <Col xs={12}>
                <ModalWarning message='Your quotes have been refreshed to reflect the information you provided. Take a moment to review the updated results and choose the product that best aligns with your needs.' />
              </Col>
            }

            <BackNext
              hideBack={props.hideBack}
              onBack={props.onBack}
              hide={props.hide}
              continueLabel={props.continueLabel || { text: (props.isPurlVerification && purlMismatch ? 'Back to Results' : 'Continue') }}
            />
          </Form>
        </FormProvider>
      </div>
    </ThemeProvider>
  );
});

BasicInfo.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  hideBack: PropTypes.bool,
  ageSpecs: PropTypes.object.isRequired,
  tobaccoOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.any.isRequired,
    label: PropTypes.any.isRequired,
  })),
  tobaccoToTrueFalseString: PropTypes.func,
  onBack: PropTypes.func,
  brandTheme: PropTypes.string,
  validateBMI: PropTypes.func,
  openEditPopUp: PropTypes.bool,
  setOpenEditPopUp: PropTypes.func,
  isPurlVerification: PropTypes.bool,
  onDeclined: PropTypes.func,
  unavailableStates: PropTypes.array,
  isTobaccoVerbiage: PropTypes.bool,
  hide: PropTypes.func,
  continueLabel: PropTypes.any,
  setModalSaveQuoteIsOpen: PropTypes.func,
  setSaveQuoteData: PropTypes.func,
  saveQuoteContact: PropTypes.object,
  emailCapture: PropTypes.object,
  setEmailCaptureActive: PropTypes.func,
  emailCaptureActive: PropTypes.bool,
};

BasicInfo.ValidationSchema = (ageSpecs) => {
  return object().shape({
    gender: string().required('Gender is required.'),
    birthdate: date()
      .typeError('Invalid date.')
      .max(sub(new Date(), { years: ageSpecs.min, days: ageSpecs.minAgeDays || 0 }), `Minimum age is ${ageSpecs.minAgeDays ? `${ageSpecs.minAgeDays} days` : ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .required('Birthdate is required.'),
    state: string().required('State is required.'),
    height: customValidators.string().when('isJuvenile', {
      is: true,
      then: (schema) => schema
        .required('Height is required.')
        .isValidHeightString('Invalid height.', 0),
      otherwise: (schema) => schema
        .required('Height is required.')
        .isValidHeightString('Invalid height.', 3),
    }),
    weight: string().when('isJuvenile', {
      is: true,
      then: (schema) => schema
        .required('Weight is required.'),
      otherwise: (schema) => schema
        .required('Weight is required.')
        .isValidWeightString('Please verify your weight has been entered correctly.', 78, 395),
    }),
    tobacco: string().transform(emptyStringToUndefined).when('isJuvenile', {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema
        .required('This field is required.'),
    }),
  });
};

BasicInfo.DefaultFormState = (prefill = false, basicInfo = null, juviMode = false, mctQuoterData = null, isActivePurl = false) => {
  return {
    gender: prefill ? 'M' : (basicInfo && basicInfo.gender) || '',
    birthdate: prefill ? '11/15/1980' : (basicInfo && basicInfo.birthdate) || '',
    fakeBirthdate: prefill ? '' : (basicInfo && basicInfo.fakeBirthdate) || '',
    tobacco: (prefill || isActivePurl) ? basicInfo.tobacco === 'false' ? 'false' : 'Never' : (mctQuoterData && mctQuoterData.isFromAmerico ? mctQuoterData.tobacco : (mctQuoterData ? mctQuoterData.tobacco === 'Never' ? 'false' : 'true' : '')) || '',
    state: prefill ? 'ID' : (basicInfo && basicInfo.state) || '',
    height: prefill ? '5 ft 10 in' : (basicInfo && basicInfo.height) || '',
    weight: prefill ? '175 pounds' : (basicInfo && basicInfo.weight) || '',
    healthClass: prefill ? 'P' : (basicInfo && basicInfo.healthClass) || '',
    usResident: true,
    replacePolicy: prefill ? false : (basicInfo && basicInfo.replacePolicy) || false,
    preQuoteHealthClass: (basicInfo && basicInfo.preQuoteHealthClass) || '',
    preQuoteAbbreviatedQualified: (basicInfo && basicInfo.preQuoteAbbreviatedQualified) || '',
    bmi: (basicInfo && basicInfo.bmi) || undefined,
    firstName: (basicInfo && basicInfo.firstName) || '',
    lastName: (basicInfo && basicInfo.lastName) || '',
    phone: (basicInfo && basicInfo.phone) || '',
    email: (basicInfo && basicInfo.email) || '',
    emailAddress: (basicInfo && basicInfo.email) || '',
  };
};

export default BasicInfo;
